import type { NleClipInfoMap } from '@mimir/client-common/fileSync/premiereTypes';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { isInElectron } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { getGlobalStore } from '../store';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { getUserDataPath } from '../desktop/utils';
import { DefaultDownloadAndRelinkOps } from '../nle/download/defaultDownloadAndRelinkOps';
import { DefaultKeldaPathOps } from '../nle/defaultKeldaPathOps';
import { DefaultLocalStateOps } from '@mimir/client-common/fileSync/defaultLocalStateOps';
import { DefaultNotificationOps } from '../nle/defaultNotificationOps';
import { DefaultOngoingDownloadOps } from '../nle/download/defaultOngoingDownloadOps';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import { checkOpenProjectsOnce } from './syncing';
import { DefaultColdStorageRestoreOps } from '@mimir/web/globals/defaultColdStorageOps';
import { buildPremiereRestoreParameters } from '@mimir/web/globals/premiereRestoreParameters';
import { searchState } from '@mimir/web/globals/searchState';
import { areDialogEventsRegistered } from '../globals/layout';

const CHECK_PROJECTS_INTERVAL = 3000;
let loopingAlready = false;

export async function startCheckOpenProjectsLoop(
  applicationContext: AdobePanelContext | ElectronContext,
  logger: Logger,
  nleClipInfos: NleClipInfoMap,
) {
  if (loopingAlready) {
    return;
  }

  loopingAlready = true;

  // Let's construct once the services that do not need anything from the store
  const backendClient = globalBackendClient;
  const ongoingDownloadOps = new DefaultOngoingDownloadOps();
  const notificationOps = new DefaultNotificationOps(applicationContext);
  const userDataSystemPath = getUserDataPath(applicationContext);

  const store = getGlobalStore();
  while (true) {
    if (!areDialogEventsRegistered()) {
      // If EVENTS.SHOW_DIALOG messages are not registered yet from Layout.vue,
      // then the "suggestDownloads" dialog from checkOpenProjectsOnce will not be shown
      // for the initially open projects. Just skip and wait for the next try, by
      // which time it should be registered correctly.
      await asyncTimeout(500);
      continue;
    }

    // Let's construct fresh instances of the services that need to read values from the store
    const skippedFileExtensions = (store.getters.tenantConfig as TenantConfigDto).skippedFileExtensionsForPremiere;
    const config = {
      isProxyMode: store.getters.isProxyWorkflowEnabled,
      isStandaloneMode: store.getters.selectedStorageProfile.isStandaloneMode,
      downloadPath: store.getters.downloadPath,
      archiveConfig: (store.getters.tenantConfig as TenantConfigDto).archiveConfig,
      selectedStorageProfileId: store.getters.selectedStorageProfile.storageId,
      userDataSystemPath,
      notifier: undefined,
      skippedFileExtensions,
    };
    const keldaPathOps = new DefaultKeldaPathOps(applicationContext, logger, store.getters.keldaPathMappings);
    const localStateOps = new DefaultLocalStateOps(applicationContext, logger, ongoingDownloadOps, keldaPathOps, config.downloadPath);
    const downloadToSubfolderPaths = (store.getters.featureFlags as TenantConfigDto['featureFlags'])?.keldaUsePrimaryRootSubfolders ?? false;

    const premiereRestoreParameters = buildPremiereRestoreParameters(applicationContext, logger, localStateOps);
    const coldStorageRestoreOps = new DefaultColdStorageRestoreOps(premiereRestoreParameters, searchState);
    const downloadAndRelinkOps = new DefaultDownloadAndRelinkOps(applicationContext, notificationOps, undefined, logger, ongoingDownloadOps, localStateOps, coldStorageRestoreOps, store.getters.downloadPath, userDataSystemPath, downloadToSubfolderPaths, store.getters.usePremierePreferenceForScaleToFrame, skippedFileExtensions);

    const services = {
      applicationContext,
      logger,
      notificationOps,
      localStateOps,
      ongoingDownloadOps,
      backendClient,
      downloadAndRelinkOps,
      coldStorageRestoreOps,
    };

    try {
      await checkOpenProjectsOnce(services, config, nleClipInfos);
    } catch (err) {
      /*
        Continue checking later, hoping that any errors are just temporary.
      */
      console.error(err);
    }

    await asyncTimeout(CHECK_PROJECTS_INTERVAL);

    // If the switch is turned off to go back to regular Desktop mode. Don't continue
    // to poll for projects since that only gets errors.
    if (isInElectron(applicationContext) && !store.getters.dvrMode) {
      console.log('Stopping to poll open projects since we are no longer connected to DVR.');
      break;
    }
  }

  loopingAlready = false;
}
